import JSZip from 'jszip';
import { supabase } from '../lib/supabase';

export interface DocumentField {
  id: string;
  label: string;
  docs: string[];
}

export interface Worker {
  id?: string;
  name: string;
  licence: string;
  date: string;
  role: string;
  whiteCardNumber?: string;
}

export interface DocumentData {
  [key: string]: string;
}

export const ALL_FIELDS: DocumentField[] = [
  { id: 'site-address', label: 'Site / Job Address', docs: ['swms', 'arcp', 'demo'] },
  { id: 'site-name', label: 'Site Name', docs: ['arcp'] },
  { id: 'date', label: 'Document Date', docs: ['swms', 'demo'] },
  { id: 'start-date', label: 'Start Date', docs: ['arcp'] },
  { id: 'business-contact', label: 'Business Contact', docs: ['swms', 'demo'] },
  { id: 'business-phone', label: 'Business Phone', docs: ['swms', 'demo'] },
  { id: 'removalist-name', label: 'Removalist Company', docs: ['arcp'] },
  { id: 'licence-number', label: 'Asbestos Licence #', docs: ['arcp'] },
  { id: 'prepared-by', label: 'Prepared By', docs: ['arcp'] },
  { id: 'assessor', label: 'Assessor', docs: ['arcp'] },
  { id: 'email', label: 'Contact Email', docs: ['arcp'] },
  { id: 'contact-phone', label: 'Contact Phone', docs: ['swms', 'arcp', 'demo'] },
  { id: 'pc-name', label: 'Principal Contractor', docs: ['swms', 'demo'] },
  { id: 'pc-phone', label: 'PC Phone', docs: ['swms', 'demo'] },
  { id: 'pc-address', label: 'PC Address', docs: ['swms', 'demo'] },
  { id: 'supervisor', label: 'Supervisor', docs: ['swms', 'arcp'] },
  { id: 'responsible-person', label: 'Responsible Person', docs: ['demo'] },
];

interface ReplacementRule {
  find: string;
  fieldId: string;
  wrapWith?: string;
}

const RULES: Record<string, ReplacementRule[]> = {
  swms: [
    { find: 'Brendon Harris', fieldId: 'business-contact' },
    { find: '0488 424 568', fieldId: 'business-phone' },
    { find: 'Dharriwaa Elders Group Incorporated', fieldId: 'pc-name' },
    { find: '174 Fox St Walgett NSW 2832', fieldId: 'pc-address' },
    { find: '17/02/2026', fieldId: 'date' },
    { find: '0448 098 418', fieldId: 'pc-phone' },
    { find: '0473 802 119', fieldId: 'contact-phone' },
    { find: '82 Fox St Walgett', fieldId: 'site-address' },
    { find: 'Bun Khem', fieldId: 'supervisor' },
  ],
  arcp: [
    { find: 'Natalie Jezard-Clarke', fieldId: 'prepared-by' },
    { find: 'Fox St Walgett', fieldId: 'site-address' },
    { find: 'Castlereagh Hire Pty Ltd', fieldId: 'removalist-name' },
    { find: 'AD213479', fieldId: 'licence-number' },
    { find: 'AD21347', fieldId: 'licence-number' },
    { find: 'Bun Khem', fieldId: 'supervisor' },
    { find: '0473 802 119', fieldId: 'contact-phone' },
    { find: '802 119', fieldId: 'contact-phone' },
    { find: 'Phil Clifton', fieldId: 'assessor' },
    { find: 'Brendon Harris', fieldId: 'business-contact' },
    { find: 'admin@castlereaghhire.com.au', fieldId: 'email' },
    { find: '0488424568', fieldId: 'business-phone' },
    { find: '488424568', fieldId: 'business-phone' },
    { find: '17/02/2026', fieldId: 'date' },
    { find: '17/02/2025', fieldId: 'start-date' },
  ],
  demo: [
    { find: 'Brendon Harris', fieldId: 'business-contact' },
    { find: '0488424568', fieldId: 'business-phone' },
    { find: '0488 424 568', fieldId: 'business-phone' },
    { find: 'Land And Housing Corporation', fieldId: 'pc-name' },
    { find: '12 Dary St Parramatta NSW 2250', fieldId: 'pc-address' },
    { find: '02/02/2025', fieldId: 'date' },
    { find: '0404 654 578', fieldId: 'pc-phone' },
    { find: '0488 424 568', fieldId: 'contact-phone' },
    { find: '64 Gallipoli St Temora NSW 2666', fieldId: 'site-address' },
    { find: ': Brendon Harris', fieldId: 'responsible-person', wrapWith: ': {v}' },
    { find: 'PC Address: 12 Dary St Parramatta NSW 2250', fieldId: 'pc-address', wrapWith: 'PC Address: {v}' },
  ],
};

const DOC_FILE: Record<string, string> = {
  swms: 'asbestos_licensed_removal_swms_10010-7_1.docx',
  arcp: 'asbestos_removal_control_plan.docx',
  demo: 'Demolition_SWMS.docx',
};

const DOC_NAME: Record<string, string> = {
  swms: 'Asbestos_SWMS',
  arcp: 'Asbestos_Control_Plan',
  demo: 'Demolition_SWMS',
};

function xmlEncode(str: string): string {
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function safeReplace(xml: string, find: string, replacement: string): string {
  // Try exact match first
  if (xml.includes(find)) {
    return xml.split(find).join(replacement);
  }

  // Try XML encoded version
  const encoded = xmlEncode(find);
  if (xml.includes(encoded)) {
    return xml.split(encoded).join(xmlEncode(replacement));
  }

  // Try trimmed version
  const trimmed = find.trim();
  if (trimmed !== find && xml.includes(trimmed)) {
    return xml.split(trimmed).join(replacement.trim());
  }

  // For text split across XML runs, look for the text within w:t tags
  // This is safer than trying to match across arbitrary XML
  const textPattern = find.split('').map(char => {
    const escaped = char.replace(/[\\^$*+?.()|[\]{}]/g, '\\$&');
    return escaped;
  }).join('(?:</w:t>(?:<[^>]*>)*<w:t(?:[^>]*)>)?');

  try {
    const regex = new RegExp(textPattern, 'g');
    if (regex.test(xml)) {
      // Reset regex
      xml = xml.replace(new RegExp(textPattern, 'g'), replacement);
    }
  } catch (error) {
    console.warn('Could not replace with regex:', find, error);
  }

  return xml;
}

function doReplace(xml: string, docType: string, data: DocumentData): string {
  const rules = RULES[docType] || [];

  for (const rule of rules) {
    const val = data[rule.fieldId];
    if (!val) continue;

    const replacement = rule.wrapWith ? rule.wrapWith.replace('{v}', val) : val;
    const find = rule.find;

    if (xml.includes(replacement)) continue;

    xml = safeReplace(xml, find, replacement);
  }

  return xml;
}

function addWorkersToTable(xml: string, workers: Worker[]): string {
  console.log('[addWorkersToTable] Called with', workers.length, 'workers');
  if (workers.length === 0) return xml;

  const threeRowPattern = /<w:tr[^>]*>[\s\S]*?<w:t>Construction Card<\/w:t>[\s\S]*?<\/w:tr>[\s\S]*?<w:tr[^>]*>[\s\S]*?<\/w:tr>[\s\S]*?<w:tr[^>]*>[\s\S]*?<\/w:tr>/;
  const allThreeRowGroups = xml.match(new RegExp(threeRowPattern.source, 'g'));

  console.log('[addWorkersToTable] Found', allThreeRowGroups?.length || 0, 'row groups in template');

  if (!allThreeRowGroups || allThreeRowGroups.length === 0) {
    console.warn('Could not find worker row groups in SWMS Sign-off table');
    return xml;
  }

  const firstGroup = allThreeRowGroups[0];
  const firstGroupIndex = xml.indexOf(firstGroup);

  const firstRowPattern = /<w:tr[^>]*>[\s\S]*?<w:t>Construction Card<\/w:t>[\s\S]*?<\/w:tr>/;
  const firstRowMatch = firstGroup.match(firstRowPattern);

  if (!firstRowMatch) {
    console.warn('Could not extract first row from group');
    return xml;
  }

  const firstRow = firstRowMatch[0];
  const cells = firstRow.match(/<w:tc[^>]*>[\s\S]*?<\/w:tc>/g);

  if (!cells || cells.length < 7) {
    console.warn(`Could not extract enough table cells. Found ${cells?.length || 0} cells, need 7`);
    return xml;
  }

  const workerRowGroups: string[] = [];

  for (const worker of workers) {
    const whiteCardNumber = worker.licence || worker.whiteCardNumber || '';
    console.log('[addWorkersToTable] Processing worker:', worker.name, 'role:', worker.role, 'card:', whiteCardNumber);

    const nameCell = cells[0].replace(/<w:t[^>]*>[\s\S]*?<\/w:t>/g, `<w:t>${xmlEncode(worker.name)}</w:t>`);
    const roleCell = cells[1].replace(/<w:t[^>]*>[\s\S]*?<\/w:t>/g, `<w:t>${xmlEncode(worker.role)}</w:t>`);

    const typeDescCell = cells[2].replace(/<w:t>Construction Card<\/w:t>/g, `<w:t>Construction Card</w:t>`);
    const classCell = cells[3];
    const numberCell = cells[4].replace(/<w:t[^>]*>[\s\S]*?<\/w:t>/g, `<w:t>${xmlEncode(whiteCardNumber)}</w:t>`);

    const dateCell = cells[5].replace(/<w:t[^>]*>[\s\S]*?<\/w:t>/g, `<w:t>${xmlEncode(worker.date)}</w:t>`);
    const signatureCell = cells[6];

    const row1 = `<w:tr>${nameCell}${roleCell}${typeDescCell}${classCell}${numberCell}${dateCell}${signatureCell}</w:tr>`;

    const continueNameCell = nameCell.replace(/<w:vMerge w:val="restart"\/>/g, '<w:vMerge/>');
    const continueRoleCell = roleCell.replace(/<w:vMerge w:val="restart"\/>/g, '<w:vMerge/>');
    const continueDateCell = dateCell.replace(/<w:vMerge w:val="restart"\/>/g, '<w:vMerge/>');
    const continueSignatureCell = signatureCell.replace(/<w:vMerge w:val="restart"\/>/g, '<w:vMerge/>');

    const emptyTypeCell = cells[2].replace(/<w:t[^>]*>[\s\S]*?<\/w:t>/g, `<w:t></w:t>`);

    const row2 = `<w:tr>${continueNameCell}${continueRoleCell}${emptyTypeCell}${classCell}${cells[4]}${continueDateCell}${continueSignatureCell}</w:tr>`;
    const row3 = `<w:tr>${continueNameCell}${continueRoleCell}${emptyTypeCell}${classCell}${cells[4]}${continueDateCell}${continueSignatureCell}</w:tr>`;

    workerRowGroups.push(row1 + row2 + row3);
  }

  const lastGroup = allThreeRowGroups[allThreeRowGroups.length - 1];
  const lastGroupEndIndex = xml.indexOf(lastGroup) + lastGroup.length;

  console.log('[addWorkersToTable] Replacing', allThreeRowGroups.length, 'template groups with', workers.length, 'worker groups');
  console.log('[addWorkersToTable] First group starts at:', firstGroupIndex);
  console.log('[addWorkersToTable] Last group ends at:', lastGroupEndIndex);

  xml = xml.slice(0, firstGroupIndex) + workerRowGroups.join('') + xml.slice(lastGroupEndIndex);

  console.log('[addWorkersToTable] Replacement complete');
  return xml;
}

async function loadTemplate(filename: string): Promise<ArrayBuffer> {
  const { data, error } = await supabase.storage
    .from('document-templates')
    .download(filename);

  if (error) throw new Error(`Failed to load template ${filename}: ${error.message}`);

  return data.arrayBuffer();
}

export async function generateDocument(
  docType: string,
  data: DocumentData,
  workers: Worker[],
  siteId?: string
): Promise<{ blob: Blob; filename: string; documentId?: string }> {
  try {
    const templateFile = DOC_FILE[docType];
    const arrayBuffer = await loadTemplate(templateFile);

    const zip = await JSZip.loadAsync(arrayBuffer);

    let xml = await zip.file('word/document.xml')?.async('string');
    if (!xml) throw new Error('Could not extract document.xml from template');

    xml = doReplace(xml, docType, data);

    if (docType === 'swms' && workers.length > 0) {
      xml = addWorkersToTable(xml, workers);
    }

    zip.file('word/document.xml', xml);

    const blob = await zip.generateAsync({
      type: 'blob',
      compression: 'DEFLATE',
    });

    const slug = (data['site-address'] || 'job').replace(/[^a-zA-Z0-9]/g, '_').slice(0, 20);
    const dateStr = (data['date'] || data['start-date'] || '').replace(/\//g, '-');
    const filename = `${DOC_NAME[docType]}_${slug}_${dateStr || 'filled'}.docx`;

    let documentId: string | undefined;

    if (siteId) {
      const { data: { user } } = await supabase.auth.getUser();

      const { data: generatedDoc, error: docError } = await supabase
        .from('generated_documents')
        .insert({
          site_id: siteId,
          document_type: docType === 'swms' ? 'asbestos_swms' :
                        docType === 'arcp' ? 'asbestos_removal_control_plan' :
                        'demolition_swms',
          file_url: '',
          generated_by: user?.id,
          field_data: data,
        })
        .select()
        .single();

      if (docError) {
        console.error('Error saving document metadata:', docError);
      } else if (generatedDoc) {
        documentId = generatedDoc.id;

        if (workers.length > 0) {
          const workerRecords = workers.map(w => ({
            document_id: documentId,
            worker_id: w.id,
            worker_name: w.name,
            licence_number: w.licence,
            worker_role: w.role,
            assignment_date: w.date,
          }));

          const { error: workersError } = await supabase
            .from('document_workers')
            .insert(workerRecords);

          if (workersError) {
            console.error('Error saving document workers:', workersError);
          }
        }
      }
    }

    return { blob, filename, documentId };
  } catch (error) {
    console.error('Document generation error:', error);
    throw error;
  }
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function formatDateForInput(date: Date): string {
  return date.toISOString().split('T')[0];
}

export function formatDateForDocument(dateString: string): string {
  if (!dateString) return '';
  const date = new Date(dateString + 'T00:00:00');
  return date.toLocaleDateString('en-AU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}
